import { memo, MutableRefObject, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha
} from "@mui/material";
import Iconify from "../../../minimals/components/iconify";
import { logEvent } from "../../utils/analyticsLogger";
import { OrderDocumentCategory, OrderDocumentDetail } from "../types";
import { DocumentPreviewDialog } from "./DocumentPreviewDialog";

const getSubcategoryKey = (categoryName: string, subcategoryId: string) => `${categoryName}:${subcategoryId}`;
// Distinct prefix so a category entry can share sectionRefs with its subcategories.
const getCategoryKey = (categoryName: string) => `cat::${categoryName}`;

// Stable, DOM-safe ids so the mobile category index can scrollIntoView by getElementById
// (mobile sections don't share the desktop sectionRefs map).
const domSlug = (value?: string | null) => (value ?? "").replace(/[^a-zA-Z0-9]+/g, "-");
export const getMobileCategoryDomId = (categoryName: string) => `mobile-cat-${domSlug(categoryName)}`;
export const getMobileSubcategoryDomId = (categoryName: string, subcategoryId: string) =>
  `mobile-subcat-${domSlug(categoryName)}-${domSlug(subcategoryId)}`;

const formatDate = (value: string) => {
  if (!value) {
    return "";
  }

  return format(new Date(value), "MM/dd/yyyy h:mm a");
};

function PublicDocumentIcon({ isPublic }: { isPublic: boolean }) {
  return (
    <svg
      version="1.0"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 144 145"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      <g transform="translate(0,145) scale(0.1,-0.1)" fill={isPublic ? "rgb(238, 114, 90)" : "#707898"} stroke="none">
        <path d="M651 1391 c-11 -7 -12 -23 -7 -71 6 -55 12 -66 47 -99 29 -28 39 -46 39 -69 0 -27 7 -34 68 -67 90 -50 184 -121 257 -194 55 -56 63 -61 103 -61 42 0 43 1 67 53 25 57 65 181 65 202 0 7 -32 47 -70 88 -74 80 -165 143 -252 178 -101 40 -281 62 -317 40z" />
        <path d="M484 1355 c-68 -25 -194 -101 -194 -116 0 -5 19 -9 43 -9 23 0 73 -7 110 -15 64 -13 69 -13 87 5 11 11 25 20 30 20 11 0 14 98 4 124 -8 20 -6 20 -80 -9z" />
        <path d="M174 1119 c-78 -109 -124 -249 -124 -375 0 -72 0 -73 39 -106 45 -38 134 -94 178 -110 26 -10 34 -8 61 12 17 12 45 23 63 24 30 1 35 6 65 72 37 78 77 211 96 309 l11 64 -36 41 c-21 22 -37 49 -37 59 0 24 -64 40 -195 48 l-90 6 -31 -44z" />
        <path d="M673 1030 c-28 -13 -33 -22 -43 -80 -17 -99 -57 -232 -100 -332 -37 -86 -38 -93 -24 -119 14 -28 17 -29 88 -29 132 0 316 66 417 150 34 27 36 33 32 73 -3 25 0 54 6 66 11 20 6 28 -48 81 -68 66 -183 150 -251 184 -42 20 -47 21 -77 6z" />
        <path d="M1326 928 c-11 -29 -29 -74 -42 -99 -15 -30 -19 -50 -13 -60 12 -20 11 -96 -2 -117 -9 -15 -5 -33 25 -95 20 -42 36 -81 36 -87 0 -5 4 -10 9 -10 5 0 19 37 32 83 28 103 30 250 4 347 -25 94 -29 97 -49 38z" />
        <path d="M1055 560 c-122 -97 -283 -157 -445 -167 -80 -5 -99 -9 -120 -28 -37 -33 -68 -47 -100 -43 -24 2 -35 -6 -71 -51 l-43 -54 30 -24 c93 -76 239 -131 374 -140 212 -14 448 90 569 250 l41 54 -20 57 c-10 31 -31 83 -46 116 -25 56 -29 60 -60 60 -18 0 -38 2 -46 4 -7 3 -35 -13 -63 -34z" />
        <path d="M74 534 c11 -46 82 -186 116 -227 l32 -37 38 48 c38 47 39 49 26 86 -11 31 -23 42 -80 71 -37 19 -84 46 -103 61 l-36 27 7 -29z" />
      </g>
    </svg>
  );
}

export function DateDocumentsSection({
  orderNumber,
  emailSubjectLine,
  documents,
  selectedDocumentIds,
  onToggleDocumentSelection,
  onToggleDocumentSelections,
  onDocumentsChanged
}: {
  orderNumber: string;
  emailSubjectLine?: string;
  documents: OrderDocumentDetail[];
  selectedDocumentIds: Set<number>;
  onToggleDocumentSelection: (documentId: number, selected: boolean) => void;
  onToggleDocumentSelections: (documentIds: number[], selected: boolean) => void;
  onDocumentsChanged: () => Promise<void>;
}) {
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden"
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.2,
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04)
        }}
      >
        <Typography variant="h6" sx={{ color: "primary.dark", fontWeight: 900 }}>
          Documents by Date
        </Typography>
      </Box>
      <Divider />
      <DocumentsTable
        orderNumber={orderNumber}
        emailSubjectLine={emailSubjectLine}
        documents={documents}
        selectedDocumentIds={selectedDocumentIds}
        onToggleDocumentSelection={onToggleDocumentSelection}
        onToggleDocumentSelections={onToggleDocumentSelections}
        onDocumentsChanged={onDocumentsChanged}
      />
    </Box>
  );
}

export function MobileDocumentsSection({
  categories,
  dateDocuments,
  viewMode,
  orderNumber,
  selectedDocumentIds,
  onToggleDocumentSelection
}: {
  categories: OrderDocumentCategory[];
  dateDocuments: OrderDocumentDetail[];
  viewMode: "category" | "date";
  orderNumber: string;
  selectedDocumentIds: Set<number>;
  onToggleDocumentSelection: (documentId: number, selected: boolean) => void;
}) {
  // One dialog for the whole list rather than one per row.
  const [previewDocument, setPreviewDocument] = useState<OrderDocumentDetail | null>(null);

  return (
    <>
      {viewMode === "date" ? (
        <Stack>
          {dateDocuments.map((document) => (
            <MobileDocumentCard
              key={document.id}
              document={document}
              orderNumber={orderNumber}
              selected={selectedDocumentIds.has(document.id)}
              onToggleDocumentSelection={onToggleDocumentSelection}
              onPreview={setPreviewDocument}
            />
          ))}
        </Stack>
      ) : (
        <Stack spacing={1.25}>
          {categories.map((category) => (
            <MobileCategoryGroup
              key={category.name}
              category={category}
              orderNumber={orderNumber}
              selectedDocumentIds={selectedDocumentIds}
              onToggleDocumentSelection={onToggleDocumentSelection}
              onPreview={setPreviewDocument}
            />
          ))}
        </Stack>
      )}

      <DocumentPreviewDialog
        document={previewDocument}
        selected={previewDocument ? selectedDocumentIds.has(previewDocument.id) : false}
        onToggleDocumentSelection={onToggleDocumentSelection}
        onClose={() => setPreviewDocument(null)}
      />
    </>
  );
}

function MobileCategoryGroup({
  category,
  orderNumber,
  selectedDocumentIds,
  onToggleDocumentSelection,
  onPreview
}: {
  category: OrderDocumentCategory;
  orderNumber: string;
  selectedDocumentIds: Set<number>;
  onToggleDocumentSelection: (documentId: number, selected: boolean) => void;
  onPreview: (document: OrderDocumentDetail) => void;
}) {
  const [open, setOpen] = useState(true);

  return (
    <Paper
      id={getMobileCategoryDomId(category.name)}
      variant="outlined"
      // Clears the fixed mobile header when jumped to from the category index.
      sx={{ borderRadius: 1, overflow: "hidden", borderColor: "#091A7A", scrollMarginTop: 80 }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((current) => !current)}
        sx={{
          width: "100%",
          border: 0,
          px: 1.5,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          gap: 1,
          // Solid fill so the category clearly outranks the tinted subcategory bands,
          // matching how the desktop list renders its category headers.
          color: "common.white",
          backgroundColor: "#091A7A",
          font: "inherit",
          textAlign: "left"
        }}
      >
        <Typography sx={{ flex: 1, fontWeight: 800 }}>{category.name}</Typography>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 26,
            height: 26,
            borderRadius: "50%",
            backgroundColor: "common.white",
            color: "#091A7A",
            flexShrink: 0
          }}
        >
          <Iconify icon={open ? "eva:chevron-down-fill" : "eva:chevron-right-fill"} width={20} />
        </Box>
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Stack>
          {category.subcategories.map((subcategory) => (
            <Box
              key={getSubcategoryKey(category.name, subcategory.id)}
              id={getMobileSubcategoryDomId(category.name, subcategory.id)}
              sx={{
                // Rule closing the subcategory (and marking the top of the next section).
                // Darker than the inset row dividers so section breaks outrank row breaks.
                borderBottom: "1px solid",
                borderColor: (theme) => alpha(theme.palette.grey[500], 0.5),
                "&:last-of-type": { borderBottom: 0 },
                scrollMarginTop: 80
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  // Tinted band with no bottom rule, so the header reads as owning the
                  // rows beneath it rather than sitting as a divider between them.
                  backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.08)
                }}
              >
                <Typography sx={{ flex: 1, color: "#333", fontSize: 15, fontWeight: 700 }}>
                  {subcategory.name}
                </Typography>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 22,
                    height: 20,
                    px: 0.75,
                    borderRadius: 999,
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    color: "primary.dark",
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: 1
                  }}
                >
                  {subcategory.documents.length}
                </Box>
              </Box>
              <Stack>
                {subcategory.documents.map((document) => (
                  <MobileDocumentCard
                    key={document.id}
                    document={document}
                    orderNumber={orderNumber}
                    selected={selectedDocumentIds.has(document.id)}
                    onToggleDocumentSelection={onToggleDocumentSelection}
                    onPreview={onPreview}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Paper>
  );
}

function MobileDocumentCard({
  document,
  orderNumber,
  selected,
  onToggleDocumentSelection,
  onPreview
}: {
  document: OrderDocumentDetail;
  orderNumber: string;
  selected: boolean;
  onToggleDocumentSelection: (documentId: number, selected: boolean) => void;
  onPreview: (document: OrderDocumentDetail) => void;
}) {
  const unavailable = document.fileExists === false;
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  return (
    // A list row rather than a card: separated by a bottom rule that's inset on the left
    // to line up with the title text (iOS-style), running to the right edge. The last row
    // in a group leaves its rule to the section below.
    <Box
      sx={{
        position: "relative",
        backgroundColor: selected ? (theme) => alpha(theme.palette.primary.main, 0.04) : "background.paper",
        "&:not(:last-of-type)::after": {
          content: '""',
          position: "absolute",
          left: 38,
          right: 0,
          bottom: 0,
          borderBottom: "1px solid",
          borderColor: (theme) => alpha(theme.palette.grey[500], 0.4)
        }
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 0.75, py: 0.35, minHeight: 48 }}>
        <Checkbox
          size="small"
          disabled={unavailable}
          checked={selected}
          onChange={(_, checked) => onToggleDocumentSelection(document.id, checked)}
          sx={{ p: 0.5 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {unavailable ? (
            <Typography sx={{ color: "error.dark", fontSize: 13, fontWeight: 400, overflowWrap: "anywhere" }}>
              {document.documentDescription || "Untitled document"}
            </Typography>
          ) : (
            <Link
              href={`/opendoc?id=${document.id}`}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              onClick={() => {
                setHasBeenOpened(true);
                logEvent(
                  "Documents Page",
                  "Document Viewed",
                  JSON.stringify({
                    orderNumber,
                    documentId: document.id,
                    category: document.category,
                    subcategory: document.subcategory
                  })
                );
                axios.post("/api/orderdocuments/lognpi", { orderNumber, documentId: document.id }).catch(() => {});
              }}
              sx={{
                color: hasBeenOpened ? "#ED7257" : "#000e48",
                fontSize: 13,
                fontWeight: 400,
                overflowWrap: "anywhere"
              }}
            >
              {document.documentDescription || "Untitled document"}
            </Link>
          )}
          {/* Page count rides with the date rather than the icon row, which frees that
              row's width for touch-sized preview/download targets. */}
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.15 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11.5 }}>
              {formatDate(document.date1)}
            </Typography>
            {!!document.numPages && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 11.5, whiteSpace: "nowrap" }}
              >
                {document.numPages} {document.numPages === 1 ? "pg" : "pgs"}
              </Typography>
            )}
          </Stack>
        </Box>
        <Stack spacing={0.1} alignItems="flex-end" sx={{ flexShrink: 0 }}>
          {/* 32px hit areas kept close together. The glyphs stay 19px — the padding is
              invisible, so this buys thumb-sized targets without looking heavier. */}
          <Stack direction="row" spacing={0.25} alignItems="center">
            {document.web > 0 && (
              <Box sx={{ width: 20, height: 20, display: "flex", "& svg": { width: 20, height: 20 } }}>
                <PublicDocumentIcon isPublic />
              </Box>
            )}
            {!unavailable && (
              <Tooltip title="Preview document">
                <IconButton
                  size="small"
                  onClick={() => onPreview(document)}
                  sx={{ width: 32, height: 32, p: 0 }}
                >
                  <Iconify icon="eva:eye-outline" width={19} />
                </IconButton>
              </Tooltip>
            )}
            {!unavailable && (
              <Tooltip title="Download document">
                <IconButton
                  size="small"
                  component="a"
                  href={`/opendoc/download?id=${document.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ width: 32, height: 32, p: 0 }}
                >
                  <Iconify icon="eva:download-fill" width={19} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          {document.userName && (
            <Typography
              variant="caption"
              color="text.secondary"
              // Matches the button's internal padding ((32 - 19) / 2) so the name's
              // right edge lines up with the download glyph rather than the hit box.
              sx={{ fontSize: 11.5, whiteSpace: "nowrap", pr: "6.5px" }}
            >
              {document.userName}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

export function CategoryNavigator({
  categories,
  onSelect,
  onSelectCategory
}: {
  categories: OrderDocumentCategory[];
  onSelect: (categoryName: string, subcategoryId: string) => void;
  onSelectCategory: (categoryName: string) => void;
}) {
  if (categories.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
        No categories
      </Typography>
    );
  }

  return (
    <Box sx={{ py: 0.25 }}>
      {categories.map((category) => (
        <Box key={category.name} sx={{ mb: 1 }}>
          <Box
            component="button"
            type="button"
            onClick={() => onSelectCategory(category.name)}
            sx={{
              display: "block",
              width: "100%",
              border: 0,
              borderRadius: 1,
              backgroundColor: "transparent",
              // Color lives on the button so the Typography inherits it and the
              // hover state cascades without a descendant selector.
              color: "primary.dark",
              cursor: "pointer",
              font: "inherit",
              textAlign: "left",
              px: 1,
              py: 0.25,
              "&:hover": {
                color: "#00b8d4"
              },
              "&:focus-visible": {
                outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2
              }
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: "inherit",
                fontSize: 15,
                fontWeight: 700,
                letterSpacing: 0,
                lineHeight: 1.25
              }}
            >
              {category.name}
            </Typography>
          </Box>
          <Box sx={{ ml: 1, mt: 0.25 }}>
            {category.subcategories.map((subcategory) => (
              <Box
                key={getSubcategoryKey(category.name, subcategory.id)}
                component="button"
                type="button"
                onClick={() => onSelect(category.name, subcategory.id)}
                sx={{
                  position: "relative",
                  display: "block",
                  width: "100%",
                  border: 0,
                  borderRadius: 1,
                  backgroundColor: "transparent",
                  color: "text.primary",
                  cursor: "pointer",
                  font: "inherit",
                  textAlign: "left",
                  py: 0.4,
                  pl: 2.5,
                  pr: 1,
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: "50%",
                    width: 14,
                    borderTop: (theme) => `2px solid ${alpha(theme.palette.primary.main, 0.24)}`
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    borderLeft: (theme) => `2px solid ${alpha(theme.palette.primary.main, 0.24)}`
                  },
                  "&:last-of-type::after": {
                    bottom: "50%"
                  },
                  "&:hover": {
                    color: "#00b8d4"
                  },
                  "&:focus-visible": {
                    outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2
                  }
                }}
              >
                <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 400, lineHeight: 1.35 }}>
                  {subcategory.name}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export function CategorySection({
  category,
  sectionRefs,
  themeColor,
  orderNumber,
  emailSubjectLine,
  collapsedCategoryNames,
  onToggleCategory,
  selectedDocumentIds,
  onToggleDocumentSelection,
  onToggleDocumentSelections,
  onDocumentsChanged
}: {
  category: OrderDocumentCategory;
  sectionRefs: MutableRefObject<Record<string, HTMLDivElement | null>>;
  themeColor: string;
  orderNumber: string;
  emailSubjectLine?: string;
  collapsedCategoryNames: Set<string>;
  onToggleCategory: (categoryName: string) => void;
  selectedDocumentIds: Set<number>;
  onToggleDocumentSelection: (documentId: number, selected: boolean) => void;
  onToggleDocumentSelections: (documentIds: number[], selected: boolean) => void;
  onDocumentsChanged: () => Promise<void>;
}) {
  const isCategoryCollapsed = collapsedCategoryNames.has(category.name);

  return (
    <Box
      ref={(element: HTMLDivElement | null) => {
        sectionRefs.current[getCategoryKey(category.name)] = element;
      }}
      sx={{
        // Same offset the subcategory sections use, so jumping here from the
        // category navigator clears the sticky application/document toolbars
        // instead of landing behind them.
        scrollMarginTop: { xs: 100, lg: 176 },
        border: "1px solid",
        borderColor: "#091A7A",
        borderRadius: 1,
        overflow: "hidden",
        backgroundColor: "background.paper"
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => onToggleCategory(category.name)}
        sx={{
          width: "100%",
          px: 1.5,
          py: 0.85,
          border: 0,
          cursor: "pointer",
          display: "grid",
          gridTemplateColumns: "auto minmax(0, 1fr)",
          gap: 1,
          alignItems: "center",
          textAlign: "left",
          color: "common.white",
          backgroundColor: "#091A7A",
          borderBottom: "1px solid",
          borderBottomColor: "#091A7A",
          "&:hover": {
            backgroundColor: "#111078"
          }
        }}
      >
        <Iconify icon={isCategoryCollapsed ? "eva:arrow-ios-forward-fill" : "eva:arrow-ios-downward-fill"} />
        <Typography
          variant="subtitle2"
          sx={{
            color: "common.white",
            fontSize: 18,
            fontWeight: 950,
            letterSpacing: 0,
            lineHeight: 1.2
          }}
        >
          {category.name}
        </Typography>
      </Box>

      <Collapse in={!isCategoryCollapsed} timeout="auto">
        <Stack spacing={2} sx={{ p: 1.25 }}>
          {category.subcategories.map((subcategory) => (
            <SubcategorySection
              key={subcategory.id}
              categoryName={category.name}
              subcategory={subcategory}
              orderNumber={orderNumber}
              emailSubjectLine={emailSubjectLine}
              sectionRefs={sectionRefs}
              selectedDocumentIds={selectedDocumentIds}
              onToggleDocumentSelection={onToggleDocumentSelection}
              onToggleDocumentSelections={onToggleDocumentSelections}
              onDocumentsChanged={onDocumentsChanged}
            />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

function SubcategorySection({
  categoryName,
  subcategory,
  orderNumber,
  emailSubjectLine,
  sectionRefs,
  selectedDocumentIds,
  onToggleDocumentSelection,
  onToggleDocumentSelections,
  onDocumentsChanged
}: {
  categoryName: string;
  subcategory: OrderDocumentCategory["subcategories"][number];
  orderNumber: string;
  emailSubjectLine?: string;
  sectionRefs: MutableRefObject<Record<string, HTMLDivElement | null>>;
  selectedDocumentIds: Set<number>;
  onToggleDocumentSelection: (documentId: number, selected: boolean) => void;
  onToggleDocumentSelections: (documentIds: number[], selected: boolean) => void;
  onDocumentsChanged: () => Promise<void>;
}) {
  const key = getSubcategoryKey(categoryName, subcategory.id);

  return (
    <Box
      ref={(element: HTMLDivElement | null) => {
        sectionRefs.current[key] = element;
      }}
      sx={{
        // Keep the subcategory header below the sticky application and document toolbars
        // when it is selected from the category navigator.
        scrollMarginTop: { xs: 100, lg: 176 },
        border: "1px solid",
        borderColor: alpha("#091A7A", 0.35),
        borderRadius: 1,
        overflow: "hidden",
        backgroundColor: "background.paper"
      }}
    >
      <DocumentsTable
        orderNumber={orderNumber}
        emailSubjectLine={emailSubjectLine}
        subcategoryName={subcategory.name}
        documents={subcategory.documents}
        selectedDocumentIds={selectedDocumentIds}
        onToggleDocumentSelection={onToggleDocumentSelection}
        onToggleDocumentSelections={onToggleDocumentSelections}
        onDocumentsChanged={onDocumentsChanged}
      />
    </Box>
  );
}

function DocumentsTable({
  orderNumber,
  emailSubjectLine,
  subcategoryName,
  documents,
  selectedDocumentIds,
  onToggleDocumentSelection,
  onToggleDocumentSelections,
  onDocumentsChanged
}: {
  orderNumber: string;
  emailSubjectLine?: string;
  subcategoryName?: string;
  documents: OrderDocumentDetail[];
  selectedDocumentIds: Set<number>;
  onToggleDocumentSelection: (documentId: number, selected: boolean) => void;
  onToggleDocumentSelections: (documentIds: number[], selected: boolean) => void;
  onDocumentsChanged: () => Promise<void>;
}) {
  const selectableDocuments = documents.filter((document) => document.fileExists !== false);
  const allSelected =
    selectableDocuments.length > 0 && selectableDocuments.every((document) => selectedDocumentIds.has(document.id));
  const someSelected = selectableDocuments.some((document) => selectedDocumentIds.has(document.id));
  const toggleAll = (_: unknown, checked: boolean) => {
    onToggleDocumentSelections(
      selectableDocuments.map((document) => document.id),
      checked
    );
  };

  return (
    <TableContainer>
      <Table
        size="small"
        sx={{
          "& .MuiTableCell-root": {
            fontSize: 12,
            lineHeight: 1.25
          },
          "& .MuiTableCell-paddingCheckbox": {
            width: 24,
            padding: "0 12px 0 8px !important"
          },
          "& .MuiTableCell-paddingCheckbox + .MuiTableCell-root": {
            pl: 0
          }
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              "& th": {
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                borderBottom: "1px solid",
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.18),
                color: "primary.dark",
                fontSize: 12,
                fontWeight: 800,
                py: 0.7
              }
            }}
          >
            <TableCell padding="checkbox" width={42}>
              {subcategoryName && selectableDocuments.length > 0 && (
                <Checkbox
                  size="small"
                  sx={{ p: 0.25 }}
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  onChange={toggleAll}
                />
              )}
            </TableCell>
            <TableCell>
              {subcategoryName ? (
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography
                    component="span"
                    sx={{
                      color: "primary.dark",
                      fontSize: 16,
                      fontWeight: 600,
                      lineHeight: 1.15
                    }}
                  >
                    {subcategoryName}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      color: "text.secondary",
                      fontSize: 13,
                      fontWeight: 500
                    }}
                  >
                    {documents.length} {documents.length === 1 ? "Document" : "Documents"}
                  </Typography>
                </Stack>
              ) : (
                "Document"
              )}
            </TableCell>
            <TableCell width={54}>Pg.</TableCell>
            <TableCell width={170}>Date</TableCell>
            <TableCell width={120}>User</TableCell>
            <TableCell width={190} align="right">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {documents.map((document) => (
            <DocumentTableRow
              key={document.id}
              orderNumber={orderNumber}
              emailSubjectLine={emailSubjectLine}
              document={document}
              selected={selectedDocumentIds.has(document.id)}
              onToggleDocumentSelection={onToggleDocumentSelection}
              onDocumentsChanged={onDocumentsChanged}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const DocumentTableRow = memo(function DocumentTableRow({
  orderNumber,
  emailSubjectLine,
  document,
  selected,
  onToggleDocumentSelection,
  onDocumentsChanged
}: {
  orderNumber: string;
  emailSubjectLine?: string;
  document: OrderDocumentDetail;
  selected: boolean;
  onToggleDocumentSelection: (documentId: number, selected: boolean) => void;
  onDocumentsChanged: () => Promise<void>;
}) {
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  return (
    <TableRow
      hover
      sx={{
        ...(document.fileExists === false
          ? {
              backgroundColor: (theme) => alpha(theme.palette.error.main, 0.035),
              "& td:first-of-type": {
                boxShadow: (theme) => `inset 3px 0 0 ${theme.palette.error.main}`
              }
            }
          : {
              "&:nth-of-type(even)": {
                backgroundColor: "#f5f6f8"
              }
            }),
        height: 38,
        "& td": {
          borderColor: (theme) => alpha(theme.palette.grey[500], 0.16),
          py: 0.45
        },
        // Doubled ampersand to outrank MUI's own `.MuiTableRow-hover:hover` rule, which
        // paints the theme's grey action.hover — indistinguishable from the zebra stripe.
        "&&:hover": {
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12)
        }
      }}
    >
      <TableCell padding="checkbox">
        {document.fileExists === false ? (
          <Tooltip title="This document is unavailable">
            <Box sx={{ color: "error.main", display: "inline-flex", pl: 1 }}>
              <Iconify icon="eva:alert-triangle-fill" width={20} />
            </Box>
          </Tooltip>
        ) : (
          <Checkbox
            size="small"
            sx={{ p: 0.25 }}
            checked={selected}
            onChange={(_, checked) => onToggleDocumentSelection(document.id, checked)}
          />
        )}
      </TableCell>
      <TableCell>
        <Stack spacing={0.25}>
          {document.fileExists === false ? (
            <Tooltip title="This document is unavailable. Please contact support if you need it.">
              <Typography sx={{ color: "error.dark", fontWeight: 700, overflowWrap: "anywhere" }}>
                {document.documentDescription || "Untitled document"}
              </Typography>
            </Tooltip>
          ) : (
            <Link
              href={`/opendoc?id=${document.id}`}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              fontWeight={400}
              sx={{
                color: hasBeenOpened ? "#ED7257" : "text.primary",
                lineHeight: 1.25,
                "&:hover": { color: "primary.main" }
              }}
              onClick={() => {
                setHasBeenOpened(true);
                logEvent(
                  "Documents Page",
                  "Document Viewed",
                  JSON.stringify({
                    orderNumber,
                    documentId: document.id,
                    category: document.category,
                    subcategory: document.subcategory
                  })
                );
                axios
                  .post("/api/orderdocuments/lognpi", {
                    orderNumber,
                    documentId: document.id
                  })
                  .catch(() => {});
              }}
            >
              {document.documentDescription || "Untitled document"}
            </Link>
          )}
        </Stack>
      </TableCell>
      <TableCell>{document.numPages || ""}</TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(document.date1)}</TableCell>
      <TableCell>
        <Link
          component="button"
          underline="hover"
          sx={{ color: "text.primary", fontWeight: 600, textAlign: "left" }}
          onClick={async () => {
            const { data } = await axios.get<string>(
              `/api/orderdocuments/getuseremail/${encodeURIComponent(document.userName)}`
            );
            if (data) {
              window.open(
                `mailto:${data}?subject=${encodeURIComponent(emailSubjectLine || document.mtaNum || orderNumber)}&body=${encodeURIComponent(
                  `Regarding ${window.location.origin}/opendoc?id=${document.id}`
                )}`
              );
            }
          }}
        >
          {document.userName}
        </Link>
      </TableCell>
      <TableCell align="right">
        <DocumentRowActions document={document} onDocumentsChanged={onDocumentsChanged} />
      </TableCell>
    </TableRow>
  );
});

function DocumentRowActions({
  document,
  onDocumentsChanged
}: {
  document: OrderDocumentDetail;
  onDocumentsChanged: () => Promise<void>;
}) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [publicLinkOpen, setPublicLinkOpen] = useState(false);
  const [note, setNote] = useState("");
  const [categoryOptions, setCategoryOptions] = useState<OrderDocumentCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [editData, setEditData] = useState(() => getEditDataFromDocument(document));
  const [editSubmitClicked, setEditSubmitClicked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const selectedCategory = categoryOptions.find((category) => category.name === editData.category);
  const selectedSubcategory = categoryOptions
    .flatMap((category) => category.subcategories)
    .find((subcategory) => subcategory.id === editData.subcategoryId);
  const descriptionOptions = selectedSubcategory?.descriptions?.filter(Boolean) ?? [];
  const quickSearchItems = useMemo(
    () =>
      categoryOptions.flatMap((category) =>
        category.subcategories.flatMap((subcategory) =>
          (subcategory.descriptions ?? []).filter(Boolean).map((description) => ({
            label: `${category.name} > ${subcategory.name} > ${description}`,
            category: category.name,
            subcategory: subcategory.name,
            subcategoryId: subcategory.id,
            description
          }))
        )
      ),
    [categoryOptions]
  );
  const selectedQuickSearchItem =
    quickSearchItems.find(
      (item) =>
        item.category === editData.category &&
        item.subcategoryId === editData.subcategoryId &&
        item.description === editData.description
    ) ?? null;
  const editErrors = {
    mtaNum: editSubmitClicked && !editData.mtaNum.trim(),
    category: editSubmitClicked && !editData.category,
    subcategoryId: editSubmitClicked && !editData.subcategoryId,
    description: editSubmitClicked && !editData.description,
    additionalDescription:
      editSubmitClicked && editData.description === "Other" && !editData.additionalDescription.trim()
  };

  useEffect(() => {
    if (editOpen) {
      setEditData(getEditDataFromDocument(document));
      setEditSubmitClicked(false);
      setError("");
    }
  }, [document, editOpen]);

  useEffect(() => {
    if (!editOpen || categoryOptions.length) {
      return;
    }

    setCategoriesLoading(true);
    axios
      .get<OrderDocumentCategory[]>("/api/orderdocuments/categories", {
        params: { orderNumber: document.mtaNum }
      })
      .then(({ data }) => setCategoryOptions(sortDocumentCategories(data ?? [])))
      .catch(() => setCategoryOptions([]))
      .finally(() => setCategoriesLoading(false));
  }, [categoryOptions.length, document.mtaNum, editOpen]);

  const runAction = async (action: () => Promise<void>) => {
    setIsSubmitting(true);
    setError("");
    try {
      await action();
    } catch (exception) {
      setError(
        axios.isAxiosError(exception)
          ? exception.response?.data?.message || String(exception.response?.data || "The document action failed.")
          : "The document action failed."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const addNote = () =>
    runAction(async () => {
      await axios.post("/api/orderdocuments/addNote", {
        documentId: document.id,
        note
      });
      setNote("");
      setNoteOpen(false);
      await onDocumentsChanged();
    });

  const saveEdit = () =>
    runAction(async () => {
      setEditSubmitClicked(true);
      const hasErrors =
        !editData.mtaNum.trim() ||
        !editData.category ||
        !editData.subcategoryId ||
        !editData.description ||
        (editData.description === "Other" && !editData.additionalDescription.trim());
      if (hasErrors) {
        return;
      }

      const nextDescription = buildDocumentDescription(editData);
      await axios.post("/api/orderdocuments/update", {
        document: {
          ...document,
          mtaNum: editData.mtaNum,
          category: editData.category,
          subcategory: editData.subcategory,
          subcategoryId: editData.subcategoryId,
          documentDescription: nextDescription,
          textToLink: editData.textToLink
        },
        doOverwrite: document.documentDescription !== nextDescription
      });
      setEditOpen(false);
      await onDocumentsChanged();
    });

  const deleteDocument = () =>
    runAction(async () => {
      await axios.post(`/api/orderdocuments/delete/${document.id}`);
      setDeleteOpen(false);
      await onDocumentsChanged();
    });

  const togglePublic = () =>
    runAction(async () => {
      await axios.post("/api/orderdocuments/updatewebvisible", {
        documentId: document.id,
        visible: !Boolean(document.web)
      });
      await onDocumentsChanged();
    });

  const createPublicLink = () =>
    runAction(async () => {
      const { data } = await axios.get<{ url: string }>("/api/orderdocuments/generatepubliclink", {
        params: { documentId: document.id }
      });
      await navigator.clipboard?.writeText(data.url);
      setPublicLinkOpen(true);
    });

  const undoPublicLink = () =>
    runAction(async () => {
      await axios.post("/api/orderdocuments/removepubliclink", {
        documentId: document.id
      });
      setPublicLinkOpen(false);
    });

  return (
    <>
      <Stack
        direction="row"
        spacing={0.1}
        justifyContent="flex-end"
        alignItems="center"
        sx={{
          "& .MuiIconButton-sizeSmall": {
            width: 28,
            height: 28,
            p: 0.4
          }
        }}
      >
        {shouldDisplayCreatePublicLink(document) && (
          <Tooltip title="Create public link">
            <IconButton size="small" onClick={createPublicLink} disabled={isSubmitting}>
              <Iconify icon="eva:link-2-fill" />
            </IconButton>
          </Tooltip>
        )}
        {document.trackingNumber && shouldDisplayShippingChip(document.shippingStatus) && (
          <Tooltip title={`FedEx ${document.trackingNumber}`}>
            <Chip
              component="a"
              clickable
              href={`https://www.fedex.com/fedextrack/?trknbr=${document.trackingNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              icon={<Iconify icon="mdi:truck-fast-outline" width={16} />}
              label={formatShippingStatus(document.shippingStatus) || "CHECKING"}
              sx={{
                mr: 0.25,
                height: 24,
                maxWidth: 120,
                borderRadius: 1,
                fontSize: 11,
                fontWeight: 800,
                color: getShippingStatusColor(document.shippingStatus),
                borderColor: getShippingStatusColor(document.shippingStatus),
                textTransform: "uppercase",
                "& .MuiChip-icon": {
                  color: "inherit"
                },
                "& .MuiChip-label": {
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }
              }}
              variant="outlined"
            />
          </Tooltip>
        )}
        <Tooltip title="Download document">
          <IconButton
            size="small"
            component="a"
            href={`/opendoc/download?id=${document.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Iconify icon="eva:download-fill" />
          </IconButton>
        </Tooltip>
        <Tooltip title={document.notes?.length ? "View notes" : "Add note"}>
          <IconButton size="small" onClick={() => setNoteOpen(true)}>
            <Iconify icon={document.notes?.length ? "eva:message-circle-fill" : "eva:message-circle-outline"} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit document">
          <IconButton size="small" onClick={() => setEditOpen(true)}>
            <Iconify icon="eva:edit-2-outline" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete document">
          <IconButton
            size="small"
            onClick={() => setDeleteOpen(true)}
            sx={{
              color: (theme) => alpha(theme.palette.error.main, 0.62),
              "&:hover": {
                color: (theme) => alpha(theme.palette.error.main, 0.82),
                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.06)
              }
            }}
          >
            <Iconify icon="eva:trash-2-outline" />
          </IconButton>
        </Tooltip>
        <Tooltip title={document.web ? "Hide from web" : "Make visible on web"}>
          <IconButton size="small" onClick={togglePublic} disabled={isSubmitting}>
            <PublicDocumentIcon isPublic={Boolean(document.web)} />
          </IconButton>
        </Tooltip>
      </Stack>

      {noteOpen && (
        <Dialog open={noteOpen} onClose={() => setNoteOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 900 }}>Document Notes</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {document.documentDescription}
              </Typography>
              {document.notes?.length ? (
                <Stack spacing={1}>
                  {[...document.notes]
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                    .map((documentNote, index) => (
                      <Box
                        key={`${documentNote.date}-${documentNote.userName}-${index}`}
                        sx={{
                          p: 1.25,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          backgroundColor: (theme) => alpha(theme.palette.grey[500], 0.04)
                        }}
                      >
                        <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mb: 0.5 }}>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                            {documentNote.userName || "Unknown user"}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                            {formatDate(documentNote.date)}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          {documentNote.note}
                        </Typography>
                      </Box>
                    ))}
                </Stack>
              ) : (
                <Alert severity="info">No notes have been added to this document.</Alert>
              )}
              <TextField
                size="small"
                autoFocus
                multiline
                minRows={4}
                label="Add note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={() => setNoteOpen(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={addNote} disabled={isSubmitting || !note.trim()}>
              Add Note
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {editOpen && (
        <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 900 }}>Edit Document</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                size="small"
                autoFocus
                label="MTA#"
                value={editData.mtaNum}
                error={editErrors.mtaNum}
                onChange={(event) => setEditData({ ...editData, mtaNum: event.target.value })}
                fullWidth
              />
              <TextField
                size="small"
                label="Text to link"
                value={editData.textToLink}
                onChange={(event) => setEditData({ ...editData, textToLink: event.target.value })}
                fullWidth
              />
              <Autocomplete
                options={quickSearchItems}
                value={selectedQuickSearchItem}
                loading={categoriesLoading}
                getOptionLabel={(option) => option.label}
                onChange={(_, value) => {
                  if (!value) {
                    return;
                  }

                  setEditData({
                    ...editData,
                    category: value.category,
                    subcategoryId: value.subcategoryId,
                    subcategory: value.subcategory,
                    description: value.description
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="QuickSearch of Category > Subcategory > Description"
                    placeholder="Select..."
                    error={editErrors.category || editErrors.subcategoryId || editErrors.description}
                  />
                )}
              />
              <TextField
                size="small"
                select
                label="Category"
                value={editData.category}
                error={editErrors.category}
                disabled={categoriesLoading}
                onChange={(event) =>
                  setEditData({
                    ...editData,
                    category: event.target.value,
                    subcategoryId: "",
                    subcategory: "",
                    description: ""
                  })
                }
              >
                {categoryOptions.map((category) => (
                  <MenuItem key={category.name} value={category.name}>
                    {category.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                select
                label="Subcategory"
                value={editData.subcategoryId}
                error={editErrors.subcategoryId}
                disabled={!selectedCategory || categoriesLoading}
                onChange={(event) => {
                  const subcategory = selectedCategory?.subcategories.find(
                    (option) => option.id === event.target.value
                  );
                  setEditData({
                    ...editData,
                    subcategoryId: event.target.value,
                    subcategory: subcategory?.name ?? "",
                    description: ""
                  });
                }}
              >
                {(selectedCategory?.subcategories ?? []).map((subcategory) => (
                  <MenuItem key={subcategory.id} value={subcategory.id}>
                    {subcategory.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                select
                label="Description"
                value={editData.description}
                error={editErrors.description}
                disabled={!selectedSubcategory || categoriesLoading}
                onChange={(event) => setEditData({ ...editData, description: event.target.value })}
              >
                {[...descriptionOptions, "Other"].map((description) => (
                  <MenuItem key={description} value={description}>
                    {description}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Additional Description"
                value={editData.additionalDescription}
                error={editErrors.additionalDescription}
                onChange={(event) =>
                  setEditData({
                    ...editData,
                    additionalDescription: event.target.value
                  })
                }
                fullWidth
              />
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={() => setEditOpen(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={saveEdit} disabled={isSubmitting || categoriesLoading}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {deleteOpen && (
        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 900 }}>Delete Document</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Typography>Do you want to delete {document.documentDescription}?</Typography>
              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={() => setDeleteOpen(false)} disabled={isSubmitting}>
              Close
            </Button>
            <Button size="small" color="error" variant="contained" onClick={deleteDocument} disabled={isSubmitting}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {publicLinkOpen && (
        <Dialog open={publicLinkOpen} onClose={() => setPublicLinkOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 900 }}>Public Link Created</DialogTitle>
          <DialogContent dividers>
            <Typography>The public link was copied to your clipboard.</Typography>
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={undoPublicLink} disabled={isSubmitting}>
              Undo
            </Button>
            <Button size="small" variant="contained" onClick={() => setPublicLinkOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

function shouldDisplayCreatePublicLink(document: OrderDocumentDetail) {
  return ["CMTCMT", "CMTATT", "SCHMUN", "SCHSTA"].includes(document.subcategoryId?.trim());
}

function getEditDataFromDocument(document: OrderDocumentDetail) {
  return {
    mtaNum: document.mtaNum ?? "",
    textToLink: document.textToLink ?? "",
    category: document.category ?? "",
    subcategoryId: document.subcategoryId ?? "",
    subcategory: document.subcategory ?? "",
    description: document.descriptionOnly || "Other",
    additionalDescription: document.additionalDescription ?? ""
  };
}

function buildDocumentDescription(data: ReturnType<typeof getEditDataFromDocument>) {
  if (data.description === "Other") {
    return data.additionalDescription.trim();
  }

  const additionalDescription = data.additionalDescription.trim();
  return additionalDescription ? `${data.description} ${additionalDescription}` : data.description;
}

function sortDocumentCategories(categories: OrderDocumentCategory[]) {
  return [...categories]
    .sort((left, right) => left.name.localeCompare(right.name))
    .map((category) => ({
      ...category,
      subcategories: [...category.subcategories]
        .sort((left, right) => left.name.localeCompare(right.name))
        .map((subcategory) => ({
          ...subcategory,
          descriptions: [...(subcategory.descriptions ?? [])].sort((left, right) => left.localeCompare(right))
        }))
    }));
}

function shouldDisplayShippingChip(status?: string) {
  return !isShippingStatusUnavailable(status);
}

function isShippingStatusUnavailable(status?: string) {
  return status === "Shipping status unavailable";
}

function formatShippingStatus(status?: string) {
  if (!status) {
    return "";
  }

  return status.toUpperCase();
}

function getShippingStatusColor(status?: string) {
  switch (status) {
    case "Accepted":
    case "Label printed":
      return "#d94c6d";
    case "In Transit":
      return "#4d148c";
    case "Delivered":
      return "#008a00";
    case undefined:
    case "":
      return "#637381";
    default:
      return "#cd4765";
  }
}
